import {
  Button,
  Input,
  Tab,
  Table,
  TableColumn,
  TableRowEvents,
  TableRowProps,
  useModal,
} from '@keymax-dev/smartepi-ui';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useRestList from '../../../../hooks/use-rest-list';
import { EndPoints, RouterPaths } from '../../../../utils/constants';
import AddMachineCustomerModal from './add-machine';
import EditMachineCustomerModal from './edit-machine';
import MachineInfoCell from './machine-info-cell';
import MachineStatusCell from './machine-status-cell';
import { Machine } from '../../../../domain/models';

type MachineTabProps = Omit<React.ComponentProps<typeof Tab>, 'children'>;

const Strings = {
  Buttons: {
    AddMachine: 'Adicionar Máquina',
  },
  Inputs: {
    Search: 'Buscar Máquina',
  },
  Table: {
    ColumnTitles: {
      Status: 'Estado',
      Info: 'Informações',
    },
  },
};

export default function MachineTab(props: MachineTabProps): JSX.Element {
  // Hooks
  const { id } = useParams<{ id: string }>();
  const restList = useRestList<Machine.Data>(EndPoints.Machines, {
    requestFilters: [{ filter: 'customer', filterValue: id }],
  });
  const addMachineModal = useModal(<AddMachineCustomerModal customerId={id} />);
  const editMachineModal = useModal(<EditMachineCustomerModal />);
  const history = useHistory();

  // Handlers
  const rowClickHandler = (
    event: React.SyntheticEvent,
    tableItem: Machine.Data
  ): void => {
    editMachineModal.injectProps({ machine: tableItem });
    editMachineModal.onClose((reason) => {
      if (typeof reason === 'object') {
        const editedMachine = reason as Machine.Data;
        restList.updateItems([editedMachine]);
      }
    });
    editMachineModal.open();
  };
  const tableScrollHandler = (
    event: React.UIEvent<HTMLTableSectionElement>
  ): void => {
    if (
      event.currentTarget.offsetHeight + event.currentTarget.scrollTop >=
      event.currentTarget.scrollHeight - 30
    ) {
      restList.nextBookmark();
    }
  };
  const addMachineClickHandler = (): void => {
    addMachineModal.onClose((reason) => {
      if (typeof reason === 'object') {
        const newMachine = reason as Machine.Data;
        restList.updateItems([newMachine]);
      }
    });
    addMachineModal.open();
  };

  // Component Elements
  const rowEvents: TableRowEvents = {
    onClick: rowClickHandler,
  };
  const rowProps: TableRowProps = {
    style: {
      cursor: 'pointer',
    },
  };

  // Effects
  useEffect(() => {
    if (restList.requestError) {
      history.replace(RouterPaths.Customer());
    }
  }, [restList.requestError]);

  return (
    <Tab {...props}>
      <nav>
        <Button
          buttonType="icon"
          icon="plus"
          text={Strings.Buttons.AddMachine}
          invert
          onClick={addMachineClickHandler}
        />
        <Input
          containerType="outline"
          iconRight="magnify"
          placeholder={Strings.Inputs.Search}
          containerProps={{ className: '__search-bar' }}
          invert
          onBlur={(event) => restList.search(event.target.value)}
          onKeyPress={(event) =>
            event.key === 'Enter'
              ? restList.search((event.target as HTMLInputElement).value)
              : undefined
          }
        />
      </nav>
      <Table
        data={restList.list}
        config={{ rowEvents, rowProps, onScroll: tableScrollHandler }}
        loading={restList.loading}
      >
        <TableColumn name={Strings.Table.ColumnTitles.Status} minwidth="120px">
          {(item: Machine.Data) => (
            <MachineStatusCell
              status={item.lastKeepAlive >= Date.now() - 120000 ? 0 : 2}
            />
          )}
        </TableColumn>
        <TableColumn name={Strings.Table.ColumnTitles.Info} flex={1}>
          {(item: Machine.Data) => (
            <MachineInfoCell
              observation={item.observation || '-'}
              name={item.name}
            />
          )}
        </TableColumn>
      </Table>
    </Tab>
  );
}
